import { FC, useState, useEffect, memo, useMemo } from "react";
import { message, Button, Menu } from "antd";
import type { MenuProps } from "antd";
import { useNavigate, useLocation, Outlet } from "react-router-dom";
import { globalStore } from "../../utils/globalStore";
import { usePermission } from "../../hooks/useMenuPermission";
import { TeamModal } from "../../components/common/TeamModal";
import type { Menu as MenuType } from "../../service/menu/types";
import { getUserInfoApi } from "../../service/user";
import { updateBaseInfo } from "../../service/user/UserService";
import "./profile.scss";

interface Props { }

// 角色代码到中文名称的映射
const getRoleDisplayName = (roles: any[] | undefined | null): string => {
  if (!roles || roles.length === 0) {
    return '用户';
  }

  const firstRole = roles[0];
  
  // 如果是字符串数组，如 ["reviewer", "assessor"]
  if (typeof firstRole === 'string') {
    const roleNameMap: Record<string, string> = {
      'reviewer': '审核者',
      'assessor': '审核者',
      'operator': '操作者',
      'user': '普通用户',
      'admin': '管理员',
      'administrator': '管理员',
      'super_admin': '管理员',
    };
    return roleNameMap[firstRole.toLowerCase()] || '用户';
  }
  
  // 如果是对象数组，优先使用 role_key 或 code 字段进行映射（确保使用最新的映射规则）
  if (typeof firstRole === 'object' && firstRole !== null) {
    // 优先使用 role_key 或 code 字段进行映射（确保使用最新的映射规则）
    const roleKey = firstRole.role_key || firstRole.code;
    if (roleKey) {
      const roleKeyMap: Record<string, string> = {
        'reviewer': '审核者',
        'assessor': '审核者',
        'operator': '操作者',
        'user': '普通用户',
        'admin': '管理员',
        'administrator': '管理员',
        'super_admin': '管理员',
      };
      const mappedName = roleKeyMap[roleKey.toLowerCase()];
      if (mappedName) {
        return mappedName;
      }
    }
    
    // 如果没有 role_key 或 code，回退到使用 name 字段
    if (firstRole.name) {
      return firstRole.name;
    }
    
    // 最后使用 role_name 字段
    if (firstRole.role_name) {
      return firstRole.role_name;
    }
  }
  
  return '用户';
};

// 用户信息卡片组件 - 使用memo避免不必要的重新渲染
const UserInfoCard = memo<{ userInfo: UserInfo | null }>(({ userInfo }) => {
  const roleDisplayName = getRoleDisplayName(userInfo?.roles);
  
  return (
    <div className="user-card">
      <div className="user-avatar">
        <img src="/img/avator.png" alt="用户头像" className="avatar-image" />
      </div>
      <div className="user-info">
        <div className="user-details">
          <div className="username">{userInfo?.nickname || userInfo?.username || '用户'}</div>
          <div className="user-role">{roleDisplayName}</div>
        </div>
        <div className="user-email">{userInfo?.email || '暂无邮箱'}</div>
      </div>
    </div>
  );
});

interface UserInfo {
  username: string;
  nickname?: string;
  email: string | null;
  phone: string | null;
  status: boolean;
  id: number;
  roles: Array<{
    role_name?: string;
    role_key?: string;
    name?: string; // 新增name字段，对应接口返回的roles[].name
    code?: string; // 新增code字段，对应接口返回的roles[].code
    create_by?: string;
    create_time?: string;
    update_by?: string;
    update_time?: string;
    remark?: any;
    version?: any;
  }>;
  groups: any[];
  notices: any[];
}


export const Profile: FC<Props> = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { hasPermission } = usePermission();
  const [selectedKeys, setSelectedKeys] = useState<string[]>([]);
  const [openKeys, setOpenKeys] = useState<string[]>([]);
  const [profileMenus, setProfileMenus] = useState<MenuType.Menu[]>([]);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [userInfo, setUserInfo] = useState<UserInfo | null>(null);
  
  // 表单状态
  const [nickname, setNickname] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  
  // 团队管理相关状态
  const [showTeamModal, setShowTeamModal] = useState(false);
  const [teamModalType, setTeamModalType] = useState<'create'>('create');
  const [teamModalLoading, setTeamModalLoading] = useState(false);

  useEffect(() => {
    // 从globalStore获取用户信息
    const updateUserInfo = () => {
      const storedUserInfo = globalStore.getUserInfo();
      if (storedUserInfo) {
        setUserInfo(storedUserInfo);
        // 使用真实用户信息初始化表单
        setNickname(storedUserInfo.username || '');
        setPhone(storedUserInfo.phone || '');
        setEmail(storedUserInfo.email || '');
      }
    };
    
    // 从接口获取最新的用户信息（包括roles）
    const fetchUserInfo = async () => {
      try {
        const response = await getUserInfoApi();
        if (response && response.code === 0 && response.data) {
          const userData = response.data;
          
          // 获取当前 globalStore 中的用户信息，用于保留 roles 和 permissions
          const currentUserInfo = globalStore.getUserInfo();
          
          // 格式化用户信息
          const formattedUserInfo: UserInfo = {
            username: userData.username,
            nickname: userData.nickname,
            email: userData.email || null,
            phone: userData.mobile || null,
            status: userData.status === 0,
            id: userData.id,
            // 如果 getUserInfoApi 返回的 roles 为空或格式不对，保留 globalStore 中的 roles
            roles: (userData.roles && userData.roles.length > 0) 
              ? userData.roles 
              : (currentUserInfo?.roles || []),
            groups: [],
            notices: []
          };
          
          setUserInfo(formattedUserInfo);
          // 使用安全更新方式避免覆盖权限等字段
          updateBaseInfo(formattedUserInfo as any);
          // 更新表单
          setNickname(formattedUserInfo.nickname || formattedUserInfo.username || '');
          setPhone(formattedUserInfo.phone || '');
          setEmail(formattedUserInfo.email || '');
        }
      } catch (error) {
        console.error('获取用户信息失败:', error);
        // 如果接口失败，使用globalStore中的数据
        updateUserInfo();
      }
    };
    
    // 进入个人中心时获取最新用户信息
    fetchUserInfo();
    
    // 更新菜单列表的函数
    const updateMenuList = () => {
      const menuList = globalStore.getMenuList();
      console.log('所有菜单数据:', menuList);
      const profileMenu = menuList.find(menu => menu.path === '/profile');
      let profileMenus = profileMenu?.children || [];
      
      // 按 sort 属性排序（sort 值越小越靠前）
      profileMenus = [...profileMenus].sort((a, b) => {
        const sortA = a.sort ?? 0;
        const sortB = b.sort ?? 0;
        return sortA - sortB;
      });
      
      console.log('Profile菜单数据 (children):', profileMenus);
      setProfileMenus(profileMenus);
    };
    
    // 初始化菜单列表
    updateMenuList();
    
    // 订阅 globalStore 的变化，以便在昵称修改或菜单更新后自动更新
    const unsubscribe = globalStore.subscribe(() => {
      updateUserInfo();
      updateMenuList(); // 同时更新菜单列表
    });
    
    // 清理函数：取消订阅
    return () => {
      unsubscribe();
    };
  }, []);

  // 监听菜单数据变化，只在首次进入时自动显示第一个菜单
  useEffect(() => {
    if (profileMenus.length > 0 && isInitialLoad) {
      const path = location.pathname;
      if (path === '/profile') {
        // 找到第一个可用的菜单（不需要权限检查）
        const availableMenus = profileMenus.filter(menu => !menu.hidden && !menu.meta?.title?.includes('开发中'));
        
        if (availableMenus.length > 0) {
          const firstMenu = availableMenus[0];
          const firstMenuPath = firstMenu.path;
          
          if (firstMenuPath) {
            navigate(firstMenuPath, { replace: true });
          }
        }
        setIsInitialLoad(false); // 标记已完成初始加载
      }
    }
  }, [profileMenus, isInitialLoad, location.pathname, navigate]);

  // 将菜单数据转换为 Ant Design Menu 的 items 格式
  const convertMenuToItems = (menus: MenuType.Menu[]): MenuProps['items'] => {
    // 先按 sort 排序，再转换为 items
    const sortedMenus = [...menus]
      .filter(menu => !menu.hidden)
      .sort((a, b) => {
        const sortA = a.sort ?? 0;
        const sortB = b.sort ?? 0;
        return sortA - sortB;
      });
    
    return sortedMenus
      .map((menu) => {
        const menuTitle = menu.meta?.title || menu.name;
        const menuKey = menu.path || menu.name;
        const isDisabled = menuTitle.includes('开发中') || menu.hidden;
        
        // 处理图标
        let iconElement: React.ReactNode = null;
        if (menu.meta?.icon && menu.meta.icon !== '#' && menu.meta.icon !== '') {
          let iconClass = menu.meta.icon;
          
          // 如果图标类名存在但不包含 fas/far/fal/fab 等前缀，默认添加 fas 前缀
          if (!iconClass.match(/^(fas|far|fal|fab)\s/)) {
            if (iconClass.startsWith('fa-')) {
              iconClass = `fas ${iconClass}`;
            } else if (!iconClass.includes('fa-')) {
              iconClass = `fas fa-${iconClass}`;
            }
          }
          
          iconElement = <i className={iconClass} />;
        }
        
        // 如果有子菜单
        if (menu.children && menu.children.length > 0) {
          // 对子菜单按 sort 排序
          const sortedChildren = [...menu.children]
            .filter(child => !child.hidden)
            .sort((a, b) => {
              const sortA = a.sort ?? 0;
              const sortB = b.sort ?? 0;
              return sortA - sortB;
            });
          
          const childrenItems = convertMenuToItems(sortedChildren);
          
          // 如果子菜单为空（都被过滤掉了），不显示父菜单
          if (!childrenItems || childrenItems.length === 0) {
            return null;
          }
          
          return {
            key: menuKey,
            label: menuTitle,
            icon: iconElement,
            disabled: isDisabled,
            children: childrenItems,
          };
        }
        
        // 如果没有 path 或 path 为空，可能是分组或分隔符
        if (!menu.path || menu.path === '') {
          // 检查是否是纯数字（可能是分组标识）
          if (/^\d+$/.test(menu.name)) {
            return null; // 跳过纯数字名称的菜单项
          }
          return {
            type: 'divider' as const,
          };
        }
        
        return {
          key: menuKey,
          label: menuTitle,
          icon: iconElement,
          disabled: isDisabled,
        };
      })
      .filter(item => item !== null) as MenuProps['items'];
  };

  // 生成菜单 items
  const menuItems = useMemo(() => {
    return convertMenuToItems(profileMenus);
  }, [profileMenus]);

  // 根据当前路由设置选中的菜单和自动展开父菜单
  useEffect(() => {
    if (profileMenus.length === 0) {
      return;
    }
    
    const path = location.pathname;
    
    // 递归查找匹配的菜单项（包括没有组件但有子菜单的父菜单）
    const findMenuByPath = (menus: MenuType.Menu[], targetPath: string): string | null => {
      for (const menu of menus) {
        // 精确匹配路径
        if (menu.path === targetPath) {
          return menu.path || menu.name;
        }
        // 如果当前路径是菜单路径的子路径（如 /profile/system/log 是 /profile/system 的子路径）
        if (menu.path && targetPath.startsWith(menu.path + '/')) {
          // 继续在子菜单中查找
          if (menu.children && menu.children.length > 0) {
            const found = findMenuByPath(menu.children, targetPath);
            if (found) {
              return found;
            }
          }
        } else if (menu.children && menu.children.length > 0) {
          // 递归查找子菜单（即使当前菜单没有path，也要检查子菜单）
          const found = findMenuByPath(menu.children, targetPath);
          if (found) {
            return found;
          }
        }
      }
      return null;
    };
    
    const matchedKey = findMenuByPath(profileMenus, path);
    if (matchedKey) {
      setSelectedKeys([matchedKey]);
    }
    
    // 自动展开包含当前路径的所有父菜单（支持多级嵌套，包括path为空或null的父菜单）
    const findParentKeys = (menus: MenuType.Menu[], targetPath: string, parentKeys: string[] = []): string[] => {
      for (const menu of menus) {
        const menuKey = menu.path || menu.name;
        
        // 如果当前路径精确匹配菜单路径，返回所有父菜单keys
        if (menu.path === targetPath) {
          return parentKeys;
        }
        
        // 如果当前路径是菜单路径的子路径（如 /profile/log/loginlog 是 /profile/log 的子路径）
        if (menu.path && targetPath.startsWith(menu.path + '/')) {
          // 这个菜单是父菜单，需要展开
          const newParentKeys = [...parentKeys, menuKey];
          // 继续在子菜单中查找更精确的匹配
          if (menu.children && menu.children.length > 0) {
            const found = findParentKeys(menu.children, targetPath, newParentKeys);
            if (found.length > 0) {
              return found;
            }
          }
          // 如果没有找到更精确的匹配，返回当前父菜单keys
          return newParentKeys;
        }
        
        // 检查子菜单中是否有匹配的项（包括没有path但有children的父菜单，如"系统管理"、"日志管理"）
        if (menu.children && menu.children.length > 0) {
          // 先递归检查子菜单中是否有匹配的项
          const newParentKeys = [...parentKeys, menuKey];
          const childResult = findParentKeys(menu.children, targetPath, newParentKeys);
          if (childResult.length > 0) {
            // 找到了匹配的子菜单，返回包含当前菜单key的结果
            return childResult;
          }
        }
      }
      return [];
    };
    
    // 查找需要展开的所有父菜单keys
    const parentKeys = findParentKeys(profileMenus, path);
    // 始终设置openKeys，即使为空数组也要设置，以确保菜单状态正确
    setOpenKeys(parentKeys);
  }, [location.pathname, profileMenus]);

  // 处理菜单点击
  const handleMenuClick: MenuProps['onClick'] = (e) => {
    const { key } = e;
    
    // 查找对应的菜单项
    const findMenuByKey = (menus: MenuType.Menu[], targetKey: string): MenuType.Menu | null => {
      for (const menu of menus) {
        const menuKey = menu.path || menu.name;
        if (menuKey === targetKey) {
          return menu;
        }
        if (menu.children && menu.children.length > 0) {
          const found = findMenuByKey(menu.children, targetKey);
          if (found) {
            return found;
          }
        }
      }
      return null;
    };
    
    const menu = findMenuByKey(profileMenus, key as string);
    if (menu && menu.path && !menu.hidden) {
      const menuTitle = menu.meta?.title || menu.name;
      if (!menuTitle.includes('开发中')) {
        navigate(menu.path);
      }
    }
  };

  // 处理菜单展开/收起
  const handleOpenChange: MenuProps['onOpenChange'] = (keys) => {
    setOpenKeys(keys);
  };



  // 团队管理相关事件处理
  const handleCreateTeam = () => {
    setTeamModalType('create');
    setShowTeamModal(true);
  };

  const handleTeamModalCancel = () => {
    setShowTeamModal(false);
    setTeamModalLoading(false);
  };

  const handleTeamModalConfirm = async (teamId?: string, teamName?: string) => {
    setTeamModalLoading(true);
    try {
      // 处理创建团队逻辑
      console.log('创建团队:', teamName);
      // 这里可以调用创建团队的API
        await new Promise(resolve => setTimeout(resolve, 1000)); // 模拟API调用
      message.success('成功创建团队');
      // 创建成功后自动加入团队
      // setCurrentTeamId(teamId || ''); // 注释掉未定义的状态
      setShowTeamModal(false);
    } catch (error) {
      console.error('创建团队失败:', error);
      message.error('操作失败，请重试');
    } finally {
      setTeamModalLoading(false);
    }
  };

  // 渲染嵌套路由内容
  const renderMainContent = () => {
    return <Outlet />;
  };


  return (
    <div className="profile-page">
      <div className="profile-content">
        {/* 左侧边栏 - 个人中心卡片 */}
        <div className="profile-sidebar">
          <div className="sidebar-title">个人中心</div>

          {/* 用户信息卡片 */}
          <UserInfoCard userInfo={userInfo} />

          {/* 导航菜单 */}
          <div className="sidebar-menu">
            {profileMenus.length === 0 ? (
              <div className="no-menu-data">
                暂无菜单数据
              </div>
            ) : (
              <Menu
                mode="inline"
                selectedKeys={selectedKeys}
                openKeys={openKeys}
                onOpenChange={handleOpenChange}
                onClick={handleMenuClick}
                items={menuItems}
                className="profile-menu"
              />
            )}
          </div>
        </div>

        {/* 主内容区域 */}
        <div className="profile-main">
          {renderMainContent()}
        </div>
      </div>
      {/* 页脚版权信息 */}


      {/* 团队管理弹窗 */}
      <TeamModal
        visible={showTeamModal}
        type={teamModalType}
        onCancel={handleTeamModalCancel}
        onConfirm={handleTeamModalConfirm}
        loading={teamModalLoading}
      />
    </div>
  );
};
